import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Users, Phone, Mail, MapPin, RefreshCw, Check, X, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, [page, statusFilter]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await axios.get(`${API}/leads?${params.toString()}`, { withCredentials: true });
      setLeads(response.data.leads);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Błąd podczas pobierania leadów');
    } finally {
      setLoading(false);
    }
  };

  const generateLeads = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/leads/generate`, {}, { withCredentials: true });
      toast.success(`Wygenerowano ${response.data.leads_created} nowych leadów`);
      fetchLeads();
    } catch (error) {
      console.error('Error generating leads:', error);
      toast.error('Błąd podczas generowania leadów');
    } finally {
      setGenerating(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/leads/${leadId}?status=${newStatus}`, {}, { withCredentials: true });
      toast.success('Status zaktualizowany');
      fetchLeads();
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Błąd podczas aktualizacji');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new': return <Badge className="bg-blue-500">Nowy</Badge>;
      case 'contacted': return <Badge className="bg-amber-500">Skontaktowano</Badge>;
      case 'converted': return <Badge className="bg-emerald-500">Skonwertowany</Badge>;
      case 'rejected': return <Badge variant="destructive">Odrzucony</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pl-PL');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Leady sprzedażowe</h1>
          <p className="text-slate-500">Firmy bez stron WWW - potencjalni klienci ({total})</p>
        </div>
        <Button
          onClick={generateLeads}
          disabled={generating}
          className="bg-amber-500 hover:bg-amber-600"
          data-testid="generate-leads-btn"
        >
          {generating ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Generowanie...
            </>
          ) : (
            <>
              <Users className="w-4 h-4 mr-2" />
              Generuj leady
            </>
          )}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">
              {leads.filter(l => l.status === 'new').length}
            </p>
            <p className="text-sm text-slate-500">Nowe</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">
              {leads.filter(l => l.status === 'contacted').length}
            </p>
            <p className="text-sm text-slate-500">Skontaktowano</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">
              {leads.filter(l => l.status === 'converted').length}
            </p>
            <p className="text-sm text-slate-500">Skonwertowane</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-600">
              {leads.filter(l => l.status === 'rejected').length}
            </p>
            <p className="text-sm text-slate-500">Odrzucone</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Wszystkie</SelectItem>
              <SelectItem value="new">Nowe</SelectItem>
              <SelectItem value="contacted">Skontaktowano</SelectItem>
              <SelectItem value="converted">Skonwertowane</SelectItem>
              <SelectItem value="rejected">Odrzucone</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Leads List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
        </div>
      ) : leads.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 text-slate-500">
            <Users className="w-12 h-12 mb-4 text-slate-300" />
            <p>Brak leadów</p>
            <p className="text-sm">Kliknij "Generuj leady" aby znaleźć firmy bez stron WWW</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {leads.map((lead) => (
            <motion.div
              key={lead.lead_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">{lead.business_name}</h3>
                        {getStatusBadge(lead.status)}
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Badge variant="secondary">{lead.category}</Badge>
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {lead.city}
                        </span>
                        {lead.business_phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {lead.business_phone}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-2">
                        Utworzono: {formatDate(lead.created_at)}
                        {lead.contacted_at && ` • Skontaktowano: ${formatDate(lead.contacted_at)}`}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Select
                        value={lead.status}
                        onValueChange={(value) => updateLeadStatus(lead.lead_id, value)}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">Nowy</SelectItem>
                          <SelectItem value="contacted">Skontaktowano</SelectItem>
                          <SelectItem value="converted">Skonwertowany</SelectItem>
                          <SelectItem value="rejected">Odrzucony</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > 20 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Poprzednia
          </Button>
          <span className="text-sm text-slate-500">
            Strona {page} z {Math.ceil(total / 20)}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => p + 1)}
            disabled={page >= Math.ceil(total / 20)}
          >
            Następna
          </Button>
        </div>
      )}
    </div>
  );
}
